import { POS_INFINITY } from "@pdfking/math"
import type { Executor, PlanFunction } from "@pdfking/planner"
import type { PageNumber } from "@pdfking/renderer-api"
import { ContentType } from "@pdfking/structs"
import type { ExecutionContext } from "@pdfking/taskmaker"
import { MethodDispatcher, Values } from "@pdfking/tfunc"

import type { PdfEngineOptions } from "#public"
import type { SinglePagePdf } from "#public"
import type { VirtualFile } from "#public"
import type { TasksType } from "#tasks"

import { FileArray, FileLiteral } from "../../public/methods/FileNodeGUID"
import { PAGE_ORDER_STRIDE } from "../../public/types/files/mixin/PageOrder"
import { VirtualFileType } from "../../public/types/files/VirtualFileType"

// Plan building only: the engine builds these nodes for the files a caller
// handed it, TaskJson builds them for the files it read out of a list — same
// dispatch either way, so a list splits exactly like a direct upload.
type Planner = Pick<Executor<TasksType, ExecutionContext>, "plan">

// batch is the document's index in the request: split output labels every
// page with batch * PAGE_ORDER_STRIDE + position, globally unique, so order
// survives any concurrent interleaving downstream. Every page carries its
// source, which is how FilePlan cuts it again.
const dispatch: (
  file: VirtualFile,
  planner: Planner,
  options: PdfEngineOptions,
  batch: number
) => PlanFunction<SinglePagePdf> = MethodDispatcher<
  [VirtualFile, Planner, PdfEngineOptions, number],
  PlanFunction<SinglePagePdf>
>({
  // a remote file is whatever was uploaded, so it dispatches by content type:
  // an image is a single page and goes through the converter, a json is a list
  // of files and goes through the reader, everything else through the
  // splitter, which rejects non-pdf content with FILE_NOT_PDF when the plan
  // runs
  [VirtualFileType.REMOTE_FILE]: (p, { plan }, options, batch) => {
    switch (p.type) {
      case ContentType.PNG:
      case ContentType.JPEG:
        return plan.TaskConvert({ options, batch }, FileLiteral(plan, p))
      case ContentType.JSON:
        return plan.TaskJson({ options, batch }, FileLiteral(plan, p))
      default:
        // one pass over the whole file: batching pages across concurrent
        // splits only had each batch read and parse the file again
        return plan.TaskSplit(
          { options, start_page: 1 as PageNumber, end_page: POS_INFINITY as PageNumber, batch },
          FileLiteral(plan, p)
        )
    }
  },
  [VirtualFileType.MULTI_PAGE_PDF]: (p, { plan }, _options, batch) =>
    FileArray(
      plan,
      p.pages.map((page, index) => ({
        ...page,
        page_order: batch * PAGE_ORDER_STRIDE + index,
      }))
    ),
  [VirtualFileType.BINARY_PAGE_PDF]: (p, { plan }, _options, batch) =>
    FileLiteral(plan, { ...p, page_order: batch * PAGE_ORDER_STRIDE }),
  [VirtualFileType.PLOTTED_PAGE_PDF]: (p, { plan }, _options, batch) =>
    FileLiteral(plan, { ...p, page_order: batch * PAGE_ORDER_STRIDE }),
  [VirtualFileType.SINGLE_PAGE_IMAGE]: (p, { plan }, _options, batch) =>
    FileLiteral(plan, { ...p.source, page_order: batch * PAGE_ORDER_STRIDE }),
  [VirtualFileType.ZIP_FILE]: (p, planner, options, batch) => {
    const files = Values(p.files)
    // sub-files subdivide this document's range so they stay ordered between
    // themselves without spilling into the next batch's range
    return planner.plan.Preload(
      ...files.map((file, index) => SplitNode(planner, options, file, batch + index / files.length))
    )
  },
})

export const SplitNode = (
  planner: Planner,
  options: PdfEngineOptions,
  file: VirtualFile,
  batch: number
): PlanFunction<SinglePagePdf> => dispatch(file, planner, options, batch)
